<template>
<div>
    <Breadcrumbs main="Admin" title="Professional Organisations" />
    <!-- Container-fluid starts-->
    <div class="container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex flex-wrap mb-4" style="gap: 8px;">
                            <b-button v-for="tab in tabs" :key="tab.value" size="sm" :variant="activeStatus === tab.value ? 'primary' : 'outline-primary'" @click="changeTab(tab.value)">
                                {{ tab.label }}
                            </b-button>
                        </div>

                        <b-row>
                            <b-col xl="6">
                                <b-input-group class="datatable-btn">
                                    <b-form-input v-model="filter" placeholder="Type to Search"></b-form-input>
                                    <b-input-group-append>
                                        <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>

                            <b-col xl="6">
                                <b-form-group label-cols="2" label="Per page" class="mb-0 datatable-select">
                                    <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div v-if="loading" class="text-center my-4">
                            <b-spinner variant="primary"></b-spinner>
                        </div>
                        <div v-else-if="error" class="text-center my-4 text-danger">
                            Could not load organisations.
                        </div>
                        <div v-else>
                            <div class="table-responsive datatable-vue">
                                <b-table show-empty stacked="md" :items="rows" :fields="tablefields" :filter="filter" :current-page="currentPage" :per-page="perPage" @filtered="onFiltered">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <template #cell(name)="row">
                                        <div class="d-flex align-items-center" style="gap: 10px; min-width: 200px;">
                                            <img v-if="row.item.logo" :src="row.item.logo" style="height: 36px; width: 36px; border-radius: 6px; object-fit: cover;" alt="" />
                                            <router-link :to="`/admin/sp-organisation-view/${row.item.id}`">
                                                <h7>{{ row.item.name }}</h7>
                                            </router-link>
                                        </div>
                                    </template>

                                    <template #cell(members_count)="row">
                                        {{ row.item.members_count }}
                                    </template>

                                    <template #cell(created_at)="row">
                                        {{ formatDate(row.item.created_at) }}
                                    </template>

                                    <template #cell(verification_status)="row">
                                        <div style="cursor: pointer; min-width: 60px;" v-b-tooltip.hover title="Click to toggle" @click="toggleVerification(row.item)">
                                            <b-badge :variant="statusVariant(row.item.verification_status)" style="text-transform: capitalize;">{{ row.item.verification_status }}</b-badge>
                                        </div>
                                    </template>

                                    <template #cell(action)="data">
                                        <span style="display: flex; gap: 12px;">
                                            <router-link :to="`/admin/sp-organisation-view/${data.item.id}`">
                                                <feather type="eye" size="22px" style="cursor: pointer;" v-b-tooltip.hover title="View"></feather>
                                            </router-link>
                                            <feather type="edit" size="22px" style="cursor: pointer;" v-b-tooltip.hover title="Edit" @click="openEdit(data.item)"></feather>
                                            <feather type="delete" size="22px" style="color: #ff595d; cursor: pointer;" v-b-tooltip.hover title="Delete" v-b-modal.modal-org-delete @click="delete_value = data.item.id"></feather>
                                        </span>
                                    </template>
                                </b-table>
                            </div>
                        </div>

                        <b-col md="6" class="my-1 p-0">
                            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" class="my-0"></b-pagination>
                        </b-col>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Container-fluid Ends-->

    <sp-organisation-edit-modal ref="editModal" @saved="fetchList" />

    <!-- Delete Modal -->
    <b-modal id="modal-org-delete" centered hide-header @ok="handleDeleteOk">
        <div class="mt-4 mb-4" style="font-size: large;">Are you sure you want to delete this organisation?</div>
    </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { statusVariant, formatDate } from './skilled-professional-helpers';
import SpOrganisationEditModal from './sp-organisation-edit-modal.vue';

export default {
    components: { SpOrganisationEditModal },

    data() {
        return {
            tabs: [
                { label: 'Pending', value: 'pending' },
                { label: 'Verified', value: 'verified' },
                { label: 'All', value: '' }
            ],
            activeStatus: 'pending',
            tablefields: [
                { key: 'index', label: '#' },
                { key: 'name', label: 'Name', sortable: true },
                { key: 'country', label: 'Country', sortable: true },
                { key: 'registration_number', label: 'Reg. No.', sortable: true },
                { key: 'members_count', label: 'Members', sortable: true },
                { key: 'created_at', label: 'Created', sortable: true },
                { key: 'verification_status', label: 'Status', sortable: true },
                { key: 'action', label: 'Action' }
            ],
            filter: null,
            delete_value: null,
            totalRows: 1,
            currentPage: 1,
            perPage: 10,
            pageOptions: [5, 10, 15, 25]
        };
    },

    computed: {
        ...mapState({
            items: state => state.skilledProfessional.organisations,
            loading: state => state.skilledProfessional.organisationLoading,
            error: state => state.skilledProfessional.organisationError
        }),

        rows() {
            return (this.items || []).map(item => ({
                ...item,
                members_count: Array.isArray(item.members) ? item.members.length : (item.members_count || 0)
            }));
        }
    },

    watch: {
        '$route.query.status': {
            handler(status) {
                this.activeStatus = status !== undefined ? status : 'pending';
                this.fetchList();
            },
            immediate: true
        }
    },

    methods: {
        fetchList() {
            this.$store.dispatch('skilledProfessional/fetchOrganisations', {
                verification_status: this.activeStatus
            }).then(() => {
                this.totalRows = this.rows.length;
            });
        },

        changeTab(status) {
            if (status === this.activeStatus) return;
            this.$router.replace({ query: { ...this.$route.query, status } });
        },

        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },

        toggleVerification(org) {
            const next = org.verification_status === 'verified' ? 'pending' : 'verified';
            Swal.fire({
                title: `Mark as ${next}?`,
                text: next === 'verified' ? 'This does not verify the organisation\'s members. Each member is reviewed individually.' : '',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes'
            }).then(result => {
                if (!(result.isConfirmed || result.value)) return;
                this.$store.dispatch('skilledProfessional/toggleOrganisationVerification', org.id).then(response => {
                    Swal.fire({
                        title: response.code == 200 ? 'Success!' : 'Error!',
                        text: response.response,
                        icon: response.code == 200 ? 'success' : 'error'
                    });
                    this.fetchList();
                });
            });
        },

        openEdit(org) {
            this.$refs.editModal.open(org);
        },

        handleDeleteOk() {
            this.$store.dispatch('skilledProfessional/deleteOrganisation', this.delete_value).then(response => {
                Swal.fire({
                    title: response.code == 200 ? 'Success!' : 'Error!',
                    text: response.response,
                    icon: response.code == 200 ? 'success' : 'error'
                });
                this.delete_value = null;
                this.fetchList();
            });
        },

        statusVariant,
        formatDate
    }
};
</script>
